import type { FieldError, UseFormRegisterReturn } from 'react-hook-form'

import { cn } from '@moment/design-system-ui/lib/cn'
import { type ChangeEvent, type MouseEvent, useEffect, useState } from 'react'

import { Link } from '#components/_primitives/link'
import { ErrorText, SimpleInput } from '#components/controls/input'
import { Text } from '#components/controls/text'
import {
  ForgotPasswordContainer,
  InputContainer,
} from '#components/shared/styles'

interface PasswordInputProps {
  register: UseFormRegisterReturn
  error?: FieldError
  serverError?: string
  onFocus?: () => void
  onBlur?: () => void
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void
  disabled?: boolean
  onForgotPasswordClick?: () => void
  showForgotPassword?: boolean
  forgotPasswordUrl?: string
  label?: string
  placeholder?: string
  autoComplete?: string
}

export const PasswordInput = ({
  register,
  error,
  serverError,
  onFocus,
  onBlur,
  onChange,
  disabled,
  onForgotPasswordClick,
  showForgotPassword = false,
  forgotPasswordUrl,
  label = 'Password',
  placeholder = 'Enter password',
  autoComplete = 'current-password',
}: PasswordInputProps) => {
  const [enableAutoComplete, setEnableAutoComplete] = useState(false)

  // Enable autocomplete after mount so password managers don't trigger early.
  useEffect(() => {
    const timer = setTimeout(() => {
      setEnableAutoComplete(true)
    }, 150)
    return () => clearTimeout(timer)
  }, [])

  const handleForgotPasswordClick = (e: MouseEvent) => {
    e.preventDefault()
    onForgotPasswordClick?.()
  }

  const forgotPasswordTextClasses = cn(
    'text-(--color-text-tertiary) dark:text-(--color-text-secondary-contrast)',
    'group-hover:text-(--color-text-secondary)/70 dark:group-hover:text-(--color-text-primary-contrast)/70',
    'transition-colors duration-250 ease',
  )

  return (
    <InputContainer data-private>
      <SimpleInput
        type="password"
        label={label}
        placeholder={placeholder}
        error={error?.message}
        onFocus={onFocus}
        data-testid="password-input"
        {...register}
        onBlur={onBlur}
        onChange={(e) => {
          register.onChange(e) // Call react-hook-form's onChange
          onChange?.(e) // Call our custom onChange if provided
        }}
        autoFocus={!disabled}
        required
        disabled={disabled}
        autoComplete={enableAutoComplete ? autoComplete : 'off'}
      />

      {serverError && <ErrorText error={serverError} className="mt-1.5" />}

      {showForgotPassword && (
        <ForgotPasswordContainer>
          {onForgotPasswordClick ? (
            <button
              type="button"
              onClick={handleForgotPasswordClick}
              className={cn('cursor-pointer', 'block')}
            >
              <Text variant="sm" className={forgotPasswordTextClasses}>
                Forgot password?
              </Text>
            </button>
          ) : (
            <Link href={forgotPasswordUrl ?? '#'} openInNewTab={false}>
              <Text variant="sm" className={forgotPasswordTextClasses}>
                Forgot password?
              </Text>
            </Link>
          )}
        </ForgotPasswordContainer>
      )}
    </InputContainer>
  )
}
